import type { SortingState } from '@tanstack/react-table';
import classNames from 'classnames';
import mixpanel from 'mixpanel-browser';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { DiscoveryTrackingEvents } from '@revelio/core';
import type { PostingDiscoveryRow } from '@revelio/data-access';
import { Tag } from '@revelio/ui';

import { useCurrencyPreference } from '../../../../../shared/user-preferences/hooks/useCurrencyPreference';
import { GlobalAiChartPreviewDataReporter } from '../../../../global-ai-assistant/global-ai-assistant-chart-preview-data-reporter';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { FilterId, type PrimaryFilterId } from '../../../filters/types/filters';
import { dimensionSourceToFilterId } from '../../../utils/dimension-source-serialization';
import { isScreenerV2Page } from '../../../utils/routing-helpers';
import type { MultiMetricProviderResult } from '../../DataProvider/MultiMetricProvider';
import { PostingDiscoveryDataProvider } from '../../DataProvider/PostingDiscoveryDataProvider';
import { ScreenerTableMultiMetricProvider } from '../../DataProvider/ScreenerTableMultiMetricProvider';
import { SnowflakeDataProvider } from '../../DataProvider/SnowflakeDataProvider';
import { TableMultiMetricProvider } from '../../DataProvider/TableMultiMetricProvider';
import { DataProviderQueryStatus, DataType } from '../../DataProvider/types';
import {
  useReportParameters,
  useTabContentConfig,
} from '../../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../PageConfigProvider/types';
import {
  type TableRow,
  useTableRows,
  useTableSelection,
} from '../../TableSelectionProvider/TableSelectionProvider';
import { TrialUnblurProvider } from '../../TrialUnblurProvider/TrialUnblurProvider';
import { ChartFallback } from '../ChartFallback';
import {
  ChartHeader,
  ChartHeaderTitle,
  ChartHeaderTitleInfoWrapper,
  ChartTitleInfo,
} from '../ChartHeader';
import { useChart } from '../ChartProvider';
import { ChartCard, type ChartProps } from '../ChartRoot/Chart';
import { ChartActionMenuWrapper } from '../chart-menu/ChartActionMenuWrapper';
import { FullPostingInfo } from '../FullPostingInfo/FullPostingInfo';
import { transformRowToPostingData } from '../FullPostingInfo/transformRowToPostingData';
import { processMultiMetricData } from '../utils/process-multi-metric-data';
import { convertTableData } from './convert-table-data';
import { PostingDiscoveryCardView } from './PostingDiscoveryCardView';
import {
  POSTING_DISCOVERY_HIDDEN_COLUMNS,
  POSTING_DISCOVERY_NUMERIC_SORT_KEYS,
  POSTINGS_DISCOVERY_DEFAULT_COLUMNS,
  validatePostingsDiscoveryColumns,
} from './posting-discovery-columns';
import { ReportTableCard } from './ReportTableCard';
import { PopoutContentSelector } from './RowPopoutSelector';
import { ScreenerTableChartContent } from './ScreenerTableChartContent';
import { SelectAllCheckbox } from './SelectAllCheckbox';
import { Table } from './Table';
import styles from './TableChart.module.css';
import { TableChartExportMeta } from './TableChartExportMeta';
import { TableControlsProvider } from './TableControlsContext';
import { TalentDiscoveryCardView } from './TalentDiscoveryCardView';
import layoutStyles from './table-chart.module.css';
import {
  TALENT_DISCOVERY_HIDDEN_COLUMNS,
  TALENT_DISCOVERY_NUMERIC_SORT_KEYS,
  validateTalentDiscoveryColumns,
} from './talent-discovery-columns';
import type { ColumnMetaMap } from './utils/convertSeriesToTableData';
import {
  getPrimaryColumnHeader,
  TD_DEFAULT_COLUMNS,
} from './utils/table-helpers';
import { migrateColumnNames } from './utils/talent-discovery-column-mappings';
import { ViewModeToggle } from './ViewModeToggle';

type TableChartProps = Pick<
  ChartProps,
  'item' | 'onClick' | 'primaryFilter'
> & {
  title?: string;
  data?: TableRow[];
  columnMetaMap?: ColumnMetaMap;
  onSelectRow?: (data: object) => void;
};

const getPrimaryDimension = (
  revelioPageId: RevelioPageId | undefined
): PrimaryFilterId | null => {
  if (revelioPageId === RevelioPageId.COMPANY_SCREENER) {
    return FilterId.COMPANY;
  }
  if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER) {
    return FilterId.GEOGRAPHY;
  }
  if (revelioPageId === RevelioPageId.ROLE_SCREENER) {
    return FilterId.ROLE;
  }
  return null;
};

export const TableChart = (props: TableChartProps) => {
  const { revelioPageId } = useTabContentConfig();
  const { exchangeRate } = useCurrencyPreference();

  const isScreenerPage = isScreenerV2Page(revelioPageId);
  const screenerPrimaryDimension = isScreenerPage
    ? getPrimaryDimension(revelioPageId)
    : null;

  if (isScreenerPage) {
    return (
      <TableControlsProvider>
        <ScreenerTableMultiMetricProvider
          primaryDimension={screenerPrimaryDimension ?? undefined}
        >
          {({ data, status }) => (
            <ChartFallback status={status} onClick={props.onClick}>
              <ScreenerTableChartContent {...props} data={data} />
            </ChartFallback>
          )}
        </ScreenerTableMultiMetricProvider>
      </TableControlsProvider>
    );
  }

  const isPostingsDiscoveryPage =
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;
  if (isPostingsDiscoveryPage) {
    return (
      <TableControlsProvider>
        <PostingDiscoveryDataProvider exchangeRate={exchangeRate}>
          {({ data, status }) => {
            if (
              data?.tableData !== undefined &&
              data.tableData.type !== DataType.TABLE_ROWS
            )
              return <ChartFallback status={DataProviderQueryStatus.Error} />;

            return (
              <ChartFallback
                status={status}
                zIndex={10}
                onClick={props.onClick}
              >
                <TableChartContent {...props} data={data?.tableData?.data} />
              </ChartFallback>
            );
          }}
        </PostingDiscoveryDataProvider>
      </TableControlsProvider>
    );
  }

  const dataSource = props.item?.plot?.dataSource;
  const snowflakeConfig = props.item?.plot?.snowflakeConfig;

  if (dataSource === 'snowflake' && snowflakeConfig) {
    return (
      <TableControlsProvider>
        <SnowflakeDataProvider config={snowflakeConfig} chartType="table">
          {({ data, status }) => {
            const rows: TableRow[] =
              data?.type === DataType.TABLE_ROWS
                ? (data.data as TableRow[])
                : [];

            return (
              <ChartFallback
                status={status}
                onClick={props.onClick ?? undefined}
              >
                <TableChartContent
                  {...props}
                  data={rows}
                  columnMetaMap={undefined}
                />
              </ChartFallback>
            );
          }}
        </SnowflakeDataProvider>
      </TableControlsProvider>
    );
  }

  return (
    <TableControlsProvider>
      <TableMultiMetricProvider
        primaryDimension={props.primaryFilter?.id}
        secondaryDimension={props.item?.plot?.secondaryDimension}
      >
        {({ data, status }) => (
          <>
            {/* `table` routes through TableChart (not DataWrappedChart), so
                the global-assistant chart-preview overlay needs the table's
                load status reported here for the overlay to clear. */}
            <GlobalAiChartPreviewDataReporter
              itemId={props.item.id}
              branch="multi"
              status={status}
              error={null}
              data={data}
              chartType="table"
              metric={props.item?.plot?.defaultMetricOptions?.[0]}
            />
            <ChartFallback status={status} onClick={props.onClick}>
              <TableDataWithParameterAggregation {...props} data={data} />
            </ChartFallback>
          </>
        )}
      </TableMultiMetricProvider>
    </TableControlsProvider>
  );
};

type TableDataWithParameterAggregationProps = Omit<TableChartProps, 'data'> & {
  data?: MultiMetricProviderResult;
};
const TableDataWithParameterAggregation = (
  props: TableDataWithParameterAggregationProps
) => {
  const { data: rawData } = props;
  const { item } = useChart();
  const reportParameters = useReportParameters();
  const { activeFilterMap } = useFilterContext();

  const multiMetricData = useMemo(() => {
    const byMetric = rawData?.byMetric;
    if (!byMetric || Object.keys(byMetric).length === 0) {
      return rawData;
    }
    const plotParameters = item?.plot?.plotParameters ?? [];
    const aggregatedByMetric = processMultiMetricData({
      byMetric,
      plotParameters,
      reportParameters,
      activeFilterMap,
    });
    return { byMetric: aggregatedByMetric };
  }, [rawData, item?.plot?.plotParameters, reportParameters, activeFilterMap]);

  const effectiveScreenerDimension =
    item?.plot?.dimensionSource && item.plot.dimensionSource !== 'primary'
      ? dimensionSourceToFilterId(item.plot.dimensionSource)
      : undefined;

  const primaryFilter = props.primaryFilter;
  const effectivePrimaryDimension =
    effectiveScreenerDimension ?? primaryFilter?.id;

  const result = convertTableData({
    data: multiMetricData?.byMetric,
    secondaryDimension: item?.plot?.secondaryDimension,
    primaryDimension: effectivePrimaryDimension,
  });

  const data = (() => {
    if (Array.isArray(result)) {
      return result;
    }

    if (!effectiveScreenerDimension) return result.rows;

    const columnName = getPrimaryColumnHeader(effectivePrimaryDimension);
    if (columnName === 'Primary') return result.rows;

    return result.rows.map((row) => {
      if (!('Primary' in row)) return row;
      const { Primary, ...rest } = row;
      return { [columnName]: Primary, ...rest };
    });
  })();

  const columnMetaMap = (() => {
    if (Array.isArray(result)) {
      return undefined;
    }

    return result.columnMetaMap;
  })();

  return (
    <TableChartContent {...props} data={data} columnMetaMap={columnMetaMap} />
  );
};

const TableChartContent = ({
  item,
  title,
  data: tableData = [],
  columnMetaMap,
  primaryFilter,
  onClick,
  onSelectRow,
}: TableChartProps) => {
  const { variant } = useChart();
  const isReport = variant === 'report';
  const { revelioPageId } = useTabContentConfig();
  const isPostingDiscoveryPage =
    revelioPageId === RevelioPageId.POSTING_DISCOVERY;
  const isTalentDiscoveryPage =
    revelioPageId === RevelioPageId.TALENT_DISCOVERY;

  const popoutChartType =
    item?.plot?.tableRowPopout?.chartType ?? ('full-profile-info' as const);

  const getRowDetail = useMemo(() => {
    if (!isTalentDiscoveryPage && !isPostingDiscoveryPage) return undefined;
    if (isPostingDiscoveryPage) {
      return (row: TableRow) => (
        <FullPostingInfo
          data={transformRowToPostingData(row as PostingDiscoveryRow)}
        />
      );
    }
    return (row: TableRow) => (
      <PopoutContentSelector
        chartType={popoutChartType}
        rowData={row as Parameters<typeof PopoutContentSelector>[0]['rowData']}
      />
    );
  }, [isTalentDiscoveryPage, isPostingDiscoveryPage, popoutChartType]);
  const [viewMode, setViewMode] = useState<'list' | 'card'>('list');
  const [sortedTableData, setSortedTableData] = useState<TableRow[]>([]);
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const { setSelectedRows, selectedRows } = useTableSelection();
  const { setAllRows } = useTableRows();

  const handleViewModeChange = useCallback(
    (mode: 'list' | 'card') => {
      if (mode === viewMode) return;
      setViewMode(mode);
      mixpanel.track(DiscoveryTrackingEvents.VIEW_MODE_CHANGED, {
        view_mode: mode,
        page_id: revelioPageId,
      });
    },
    [revelioPageId, viewMode]
  );

  const hasActiveSorting = sortingState.length > 0;

  useEffect(() => {
    if (viewMode !== 'card' || hasActiveSorting) {
      return;
    }
    setSortedTableData(tableData);
  }, [hasActiveSorting, tableData, viewMode]);

  const exportData = useMemo(
    () =>
      tableData.length > 0
        ? { type: DataType.TABLE_ROWS, data: tableData }
        : undefined,
    [tableData]
  );

  useEffect(() => {
    setAllRows(tableData);
  }, [tableData, setAllRows]);

  const handleSortedDataChange = useCallback((rows: TableRow[]) => {
    setSortedTableData(rows);
  }, []);

  const cardData = hasActiveSorting ? sortedTableData : tableData;

  const numOfRecords = tableData.length;

  const showNumOfRecords = numOfRecords > 0;

  const tagLabel =
    numOfRecords >= 5000
      ? `More than ${numOfRecords.toLocaleString()}`
      : numOfRecords.toLocaleString();

  const onRowSelectionChange = useCallback(
    (rows: TableRow[]) => {
      setSelectedRows(rows);
    },
    [setSelectedRows]
  );

  const enableRowSelection = item?.plot?.enableRowSelection !== false;

  const cardViewProps = {
    data: cardData,
    enableRowSelection,
    onRowSelectionChange,
    selectedRows,
    popoutChartType:
      item?.plot?.tableRowPopout?.chartType ?? ('full-profile-info' as const),
  };

  let tableNumericSortKeys: Set<string> | undefined;
  let tableDefaultColumns: string[] | undefined;
  if (isTalentDiscoveryPage) {
    tableNumericSortKeys = TALENT_DISCOVERY_NUMERIC_SORT_KEYS;
    tableDefaultColumns = TD_DEFAULT_COLUMNS;
  }
  if (isPostingDiscoveryPage) {
    tableNumericSortKeys = POSTING_DISCOVERY_NUMERIC_SORT_KEYS;
    tableDefaultColumns = POSTINGS_DISCOVERY_DEFAULT_COLUMNS;
  }

  // Stable identity — feeds the Table/allKeys memos.
  const filterColumn = useMemo(() => {
    if (!isTalentDiscoveryPage && !isPostingDiscoveryPage) return () => true;
    const hidden = isPostingDiscoveryPage
      ? POSTING_DISCOVERY_HIDDEN_COLUMNS
      : TALENT_DISCOVERY_HIDDEN_COLUMNS;
    return (key: string) => !hidden.includes(key);
  }, [isTalentDiscoveryPage, isPostingDiscoveryPage]);

  const processResolvedColumns = useMemo(() => {
    if (!isTalentDiscoveryPage && !isPostingDiscoveryPage) return undefined;
    if (isPostingDiscoveryPage) return validatePostingsDiscoveryColumns;
    return (columns: string[]) =>
      validateTalentDiscoveryColumns(migrateColumnNames(columns));
  }, [isTalentDiscoveryPage, isPostingDiscoveryPage]);

  const columnStorageKey = `columns_${revelioPageId ?? 'custom_dash'}`;

  const tableContent =
    viewMode === 'card' ? (
      <div
        className={classNames(
          classNames(styles.cardViewContainer),
          layoutStyles.cardViewArea
        )}
      >
        {isPostingDiscoveryPage ? (
          <PostingDiscoveryCardView
            enableRowSelection={enableRowSelection}
            onRowSelectionChange={onRowSelectionChange}
            selectedRows={selectedRows as PostingDiscoveryRow[]}
            data={tableData as PostingDiscoveryRow[]}
          />
        ) : (
          <TalentDiscoveryCardView {...cardViewProps} />
        )}
      </div>
    ) : (
      <Table
        data={tableData}
        columnMetaMap={columnMetaMap}
        onSelectRow={onSelectRow}
        enableRowSelection={enableRowSelection}
        onSortedDataChange={
          isTalentDiscoveryPage || isPostingDiscoveryPage
            ? handleSortedDataChange
            : undefined
        }
        onSortingStateNotify={
          isTalentDiscoveryPage || isPostingDiscoveryPage
            ? setSortingState
            : undefined
        }
        filterColumn={filterColumn}
        numericSortKeys={tableNumericSortKeys}
        defaultColumns={tableDefaultColumns}
        getRowDetail={getRowDetail}
        columnStorageKey={columnStorageKey}
        processResolvedColumns={processResolvedColumns}
        isDiscoveryTable={isTalentDiscoveryPage || isPostingDiscoveryPage}
      />
    );

  const wrappedTableContent = isTalentDiscoveryPage ? (
    <TrialUnblurProvider>{tableContent}</TrialUnblurProvider>
  ) : (
    tableContent
  );

  const tableExportEnabled = Boolean(exportData);

  const exportRegistration = item?.id ? (
    <>
      <ChartActionMenuWrapper
        chartId={item.id}
        title={title || 'Table'}
        primaryFilterId={primaryFilter?.id}
        plotChartType="table"
        data={exportData}
        dataType={exportData ? DataType.TABLE_ROWS : undefined}
      />
      <TableChartExportMeta
        chartId={item.id}
        enabled={tableExportEnabled}
        columnMetaMap={columnMetaMap}
        plotFormat={item?.plot?.format}
      />
    </>
  ) : null;

  if (isReport) {
    return (
      <>
        {exportRegistration}
        <ReportTableCard title={title} onClick={onClick}>
          {wrappedTableContent}
        </ReportTableCard>
      </>
    );
  }

  const isDiscoveryPage = isTalentDiscoveryPage || isPostingDiscoveryPage;

  return (
    <>
      {exportRegistration}
      <ChartCard onClick={onClick} isRelative>
        <ChartHeader
          variant={isDiscoveryPage ? 'discovery-table' : 'table'}
          className={isDiscoveryPage ? styles.discoveryChartHeader : undefined}
        >
          <ChartHeaderTitleInfoWrapper
            {...(item.info && {
              chartTitleInfo: <ChartTitleInfo info={item.info} />,
            })}
            className={classNames(
              styles.titleRow,
              styles.headerBorder,
              isDiscoveryPage && styles.discoveryHeader
            )}
          >
            <ChartHeaderTitle>
              {enableRowSelection && tableData.length > 0 && (
                <SelectAllCheckbox
                  tableData={tableData}
                  selectedRows={selectedRows}
                  onRowSelectionChange={onRowSelectionChange}
                />
              )}
              {title}
              {showNumOfRecords &&
                (isDiscoveryPage ? (
                  <span className={styles.recordCount}>{tagLabel}</span>
                ) : (
                  <Tag
                    size="sm"
                    variant="outline"
                    className={styles.recordCountBadge}
                  >
                    {tagLabel}
                  </Tag>
                ))}
            </ChartHeaderTitle>
            {isDiscoveryPage && tableData.length > 0 && (
              <ViewModeToggle
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
              />
            )}
          </ChartHeaderTitleInfoWrapper>
        </ChartHeader>
        <div
          data-testid="card-body"
          data-discovery={isDiscoveryPage || undefined}
          className={styles.tableBody}
        >
          {wrappedTableContent}
        </div>
      </ChartCard>
    </>
  );
};
